import {JsonAdapter} from "../helpers/JsonAdapter";
import {environment} from "../../../environments/environment";
import {Organisation} from "./Organisation";
import { of } from 'rxjs';
import {Location} from "./Location";
import {Salary} from "./Salary";
import {IndustrialSector} from "./IndustrialSector";
import {OpportunityType} from "./OpportunityType";
import {EmploymentType} from "./EmploymentType";
import dayjs from 'dayjs';
import {DateTimeFormat} from "../helpers/DateTimeFormat";
import {ApplicationForm} from "./ApplicationForm";
import {Project} from "./Project";
import {ApplicationOption} from "./ApplicationOption";
import {inject} from "@angular/core";
import {EnvironmentService} from "../services/environment.service";
import {Application} from "./Application";
import {FavouriteJobGroup} from "../modules/favourite-utils/models/FavouriteJobGroup";
import {View} from "./View"
import {AreaOfInterest} from "./AreaOfInterest";
import {TypeOfWork} from "./TypeOfWork";

export class Vacancy {
  id: string = "";
  slug: string = "";
  job_title: string = "";
  job_summary: string = "";
  qualification_and_experience: string = "";
  town: string = "";
  postcode: string = "";
  additional_information: string = "";
  company_name: string = "";
  closing_date: string = "";
  created_at: string = "";
  organisation!: Organisation;
  status: string = "";
  reference_number: string = "";
  _current_user_meta!: any;
  job_location: Location[] = [];
  opportunity_type: OpportunityType[] = [];
  industrial_sector: IndustrialSector[] = [];
  employment_type: EmploymentType[] = [];
  salary?: Salary;
  is_open: boolean = false;
  application_form!: ApplicationForm;
  project?: Project;
  application_option?: ApplicationOption;
  application_online_url: string = "";
  contact_message: string = "";
  is_ongoing = false;
  location_details: string = "";
  organisation_logo_url = "";
  applications: Application[] = [];
  area_of_interest: AreaOfInterest[] = [];
  business_area: AreaOfInterest[] = [];
  type_of_work: TypeOfWork[] = [];
  views: View[] = [];
  apply_count: number = 0;
  view_count: number = 0;
  number_of_applications: number = 0;
  number_of_views: number = 0;
  favourite_count: number = 0;
  number_of_favourites: number = 0;
  outcome = "";
  outcome_details = "";
  highlighted = false;

  show_applications = false;
  show_views = false;
  favouritedBy: any[] = [];
  archived = false;
  loading = {
    list: false
  };


  static DRAFT = 'draft';
  static COMPLETED = 'completed';
  static QUERIED = 'queried';
  static CONFIRMED = 'checked_and_confirmed';
  static ADVERTISED = 'advertised';

  static statuses = of([Vacancy.DRAFT, Vacancy.COMPLETED, Vacancy.QUERIED, Vacancy.CONFIRMED, Vacancy.ADVERTISED]);

  static create(object:any, env: EnvironmentService | null = null): Vacancy {
    const item = JsonAdapter.fill(new Vacancy(), object);

    if(object.organisation) {
      item.organisation = Organisation.create(object.organisation, env);
    }

    if(object._current_user_meta) {
      item._current_user_meta = object._current_user_meta;
    }

    if(object.job_location) {
      item.job_location = Location.createMultiple(object.job_location);
    }

    if(object.opportunity_type) {
      item.opportunity_type = OpportunityType.createMultiple(object.opportunity_type);
    }

    if(object.salary) {
      item.salary = Salary.create(object.salary);
    }

    if(object.industrial_sector) {
      item.industrial_sector = IndustrialSector.createMultiple(object.industrial_sector);
    }

    if(object.type_of_work) {
      item.type_of_work = TypeOfWork.createMultiple(object.type_of_work);
    }

    if(object.business_area) {
      item.business_area = AreaOfInterest.createMultiple(object.business_area);
    }

    if(object.employment_type) {
      item.employment_type = EmploymentType.createMultiple(object.employment_type);
    }

    if(object.application_form) {
      item.application_form = ApplicationForm.create(object.application_form);
    }

    if(object.application_option) {
      item.application_option = ApplicationOption.create(object.application_option);
    }

    if(object.applications) {
      item.applications = Application.createMultiple(object.applications);
    }

    if(object.views) {
      item.views = View.createMultiple(object.views);
    }

    if(env) {
      item.organisation_logo_url = env.apiBaseUrl + 'organisation/' + item.organisation?.id + '/logo/current';
    }

    return item;
  }

  static createMultiple(objects: Vacancy[], env: EnvironmentService | null = null): Vacancy[] {
    const items = [];
    for (let i = 0; i < objects.length; i++) {
      items.push(Vacancy.create(objects[i], env));
    }
    return items;
  }

  isDraft() {
    return this.status === Vacancy.DRAFT;
  }

  isCompleted() {
    return this.status === Vacancy.COMPLETED;
  }

  isConfirmed() {
    return this.status === Vacancy.CONFIRMED;
  }

  isQueried() {
    return this.status === Vacancy.QUERIED;
  }

  getStatusDescription() {
    if(this.status === Vacancy.DRAFT) {
      return 'Draft';
    } else if(this.status === Vacancy.COMPLETED) {
        return 'Completed';
    } else if(this.status === Vacancy.QUERIED) {
      return 'Waiting for Confirmation';
    } else if(this.status === Vacancy.CONFIRMED) {
      return 'Confirmed';
    } else if(this.status === Vacancy.ADVERTISED) {
      return 'Advertised';
    }

    return "";
  }

  get url() {
    return '/organisation-hub/' + (this.organisation.slug || this.organisation.id) + '/jobs/' + (this.slug || this.id);
  }

  get oldUrl() {
    return '/jobs/' + (this.slug || this.id);
  }

  get displayedClosingDate() {
    if(!this.closing_date) {
      return 'Ongoing';
    }

    return DateTimeFormat.date(this.closing_date);
  }

  get jobLocationList() {
    return this.job_location.map(location => location.name).join(', ');
  }

  isClosingDateComingSoon() {

    const closingDateSoon = dayjs(this.closing_date).subtract(7, 'day');
    return dayjs().isAfter(closingDateSoon);
  }

  isOpen() {
    return this.is_open;
  }

  isFavourite() {
    return this._current_user_meta && this._current_user_meta.added_to_favourites;
  }

  alreadyApplied() {
    return this._current_user_meta && this._current_user_meta.already_applied_to_this_vacancy;
  }

  isApplyOnline() {
    return this.application_option && this.application_option.name === ApplicationOption.APPLY_ONLINE;
  }

  isApplyCV() {
    return this.application_option && this.application_option.name === ApplicationOption.SENDCV;
  }

  isApplyContact() {
    return this.application_option && this.application_option.name === ApplicationOption.CONTACT;
  }

  isApplyForm() {
    return this.application_option && this.application_option.name === ApplicationOption.APPLICATION_FORM;
  }

  get displayedOutcome() {
    switch (this.outcome) {
      case 'recruited_dncp':
        return 'Filled from DNCP';
      case 'recruited_internally':
        return 'Filled internally';
      case 'recruited_other':
        return 'Filled from other portal';
      case 'not_recruited':
        return 'Job not filled';
    }

    return "Select Outcome";
  }

  get jobTitleAndLocation() {
    return this.job_title + (this.job_location.length ? ' (' + this.jobLocationList + ')' : '');
  }

  get name() {
    return this.job_title;
  }
}
